import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { useNavigate, useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getDoc, getDocs, collection } from "firebase/firestore";

import Panel from "../central/Panel";
import Button from "../central/Button";

import { paths } from "../lib/paths";
import { ref } from "../lib/fw";
import { useToast } from "../context/ToastContext";
import { getLeagueConfig } from "../utils/leagueConfig";
import PageLoader from "../components/PageLoader";

const rowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "10px",
};

const statCardStyle = {
  background: "#f6f6f6",
  padding: "10px 12px",
  borderRadius: "8px",
};

const labelStyle = {
  fontSize: "0.8rem",
  color: "#777",
  marginBottom: "4px",
};

function formatLeagueType(league) {
  const rawType =
    league?.leagueType ||
    league?.format ||
    league?.teamType ||
    league?.squadType;

  if (!rawType) return "—";

  const normalized = String(rawType).trim();
  if (/^\d+$/.test(normalized)) {
    return `${normalized}-a-side`;
  }

  return normalized;
}

function getResolvedLeagueType(league) {
  return (
    league?.leagueType ||
    league?.format ||
    league?.teamType ||
    league?.squadType ||
    "11-a-side"
  );
}

function formatBudget(league, leagueConfig) {
  const raw = league?.budget ?? leagueConfig?.budget;
  if (raw === undefined || raw === null || raw === "") return "—";
  const num = Number(raw);
  return Number.isFinite(num) ? `$${num.toFixed(1)}m` : String(raw);
}

function formatSquadRules(league, leagueConfig) {
  const totalPlayers =
    leagueConfig?.squadConfig?.maxPlayers ||
    league?.squadSize ||
    league?.teamSize ||
    league?.playersPerTeam;

  const firstPositionRule = leagueConfig?.squadConfig?.positionRules
    ? Object.values(leagueConfig.squadConfig.positionRules)[0]
    : null;

  const minPerPosition =
    firstPositionRule?.min ??
    league?.minPerPosition;

  const maxPerPosition =
    firstPositionRule?.max ??
    league?.maxPerPosition;

  const parts = [];
  if (totalPlayers) parts.push(`${totalPlayers} players total`);
  if (minPerPosition !== undefined && maxPerPosition !== undefined) {
    parts.push(`min ${minPerPosition}, max ${maxPerPosition} per pos.`);
  }

  return parts.length > 0 ? parts.join("\n") : "—";
}

export default function League() {
  const { leagueID } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [league, setLeague] = useState(null);
  const [userRole, setUserRole] = useState("user");
  const [loading, setLoading] = useState(true);
  const [inviteEmails, setInviteEmails] = useState("");
  const [sendingInvites, setSendingInvites] = useState(false);

  useEffect(() => {
    const fetchLeagueData = async () => {
      setLoading(true);
      try {
        const leagueSnap = await getDoc(paths.league(leagueID));
        if (!leagueSnap.exists()) {
          setLeague(null);
          setLoading(false);
          return;
        }

        const leagueData = { id: leagueSnap.id, ...leagueSnap.data() };
        setLeague(leagueData);
        try {
          const teamsSnap = await getDocs(collection(ref("leagues", leagueID), "teams"));
          leagueData.memberCount = teamsSnap.size;
          setLeague({ ...leagueData });
        } catch (e) {
          console.error("Error fetching team count:", e);
        }

        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (currentUser?.uid) {
          const userSnap = await getDoc(ref("users", currentUser.uid));
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const roles = userData?.roles && typeof userData.roles === "object"
              ? userData.roles
              : {};
            setUserRole(roles?.[leagueID] || "user");
          } else {
            setUserRole("user");
          }
        } else {
          setUserRole("user");
        }
      } catch (error) {
        console.error("Error fetching league page data:", error);
        showToast("Error loading league page", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchLeagueData();
  }, [leagueID, showToast]);

  const parsedEmails = useMemo(() => {
    return inviteEmails
      .split(/[\n,]+/)
      .map((email) => email.trim())
      .filter(Boolean);
  }, [inviteEmails]);

  const resolvedLeagueType = getResolvedLeagueType(league);
  const leagueConfig = getLeagueConfig(resolvedLeagueType);

  const handleCopyInviteMessage = async () => {
    if (!league?.code || !league?.name) {
      showToast("Missing league info", "error");
      return;
    }

    const message = `Join our ClubWeek league: ${league.name}\nLeague code: ${league.code}\nhttps://clubweek.co/join?code=${league.code}`;

    try {
      await navigator.clipboard.writeText(message);
      showToast("Invite message copied", "success");
    } catch (e) {
      showToast("Failed to copy message", "error");
    }
  };

  const handleCopyCode = async () => {
    if (!league?.code) {
      showToast("No league code found", "error");
      return;
    }

    try {
      await navigator.clipboard.writeText(league.code);
      showToast("League code copied", "success");
    } catch (error) {
      console.error("Copy failed:", error);
      showToast("Could not copy code", "error");
    }
  };

  const handleSendInvites = async () => {
    if (!league?.code || !league?.name) {
      showToast("League details are missing", "error");
      return;
    }

    if (parsedEmails.length === 0) {
      showToast("Enter at least one email address", "error");
      return;
    }

    setSendingInvites(true);
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      const inviterName = currentUser?.displayName || currentUser?.email || "Someone";

      const functions = getFunctions(undefined, "us-central1");
      const sendLeagueInviteEmail = httpsCallable(functions, "sendLeagueInviteEmail");

      await Promise.all(
        parsedEmails.map((email) =>
          sendLeagueInviteEmail({
            email,
            leagueName: league.name,
            leagueCode: league.code,
            inviterName,
          })
        )
      );

      setInviteEmails("");
      showToast(
        parsedEmails.length === 1
          ? "Invite sent"
          : `Invites sent to ${parsedEmails.length} people`,
        "success"
      );
    } catch (error) {
      console.error("Error sending invites:", error);
      showToast("Error sending invite email", "error");
    } finally {
      setSendingInvites(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageLoader message="Loading league…" />
      </>
    );
  }

  if (!league) {
    return (
      <>
        <p>No league found.</p>
      </>
    );
  }

  return (
    <>
      <Panel style={{ marginBottom: "1rem", maxWidth: "700px" }}>
        <h2 style={{ marginTop: 0, marginBottom: "12px" }}>{league.name || leagueID}</h2>
        <div style={rowStyle}>
          <div style={statCardStyle}>
            <div style={labelStyle}>League Code</div>
            <div style={{ fontWeight: 800 }}>{league.code || "—"}</div>
          </div>
          <div style={statCardStyle}>
            <div style={labelStyle}>Season</div>
            <div style={{ fontWeight: 800 }}>{league.currentSeasonID || "—"}</div>
          </div>
          <div style={statCardStyle}>
            <div style={labelStyle}>League Type</div>
            <div style={{ fontWeight: 800 }}>{formatLeagueType(league) || resolvedLeagueType}</div>
          </div>
          <div style={statCardStyle}>
            <div style={labelStyle}>Current GW</div>
            <div style={{ fontWeight: 800 }}>{league?.currentGW || "—"}</div>
          </div>
        </div>
      </Panel>

      <Panel style={{ marginBottom: "1rem", maxWidth: "700px" }}>
        <h3 style={{ marginTop: 0, marginBottom: "10px" }}>League Info</h3>
        <div style={rowStyle}>
          <div style={statCardStyle}>
            <div style={labelStyle}>Budget</div>
            <div style={{ fontWeight: 800 }}>{formatBudget(league, leagueConfig)}</div>
          </div>
          <div style={statCardStyle}>
            <div style={labelStyle}>Squad Rules</div>
            <div style={{ fontWeight: 800, whiteSpace: "pre-line" }}>
              {formatSquadRules(league, leagueConfig)}
            </div>
          </div>
          <div style={statCardStyle}>
            <div style={labelStyle}>Your Role</div>
            <div style={{ fontWeight: 800, textTransform: "capitalize" }}>{userRole}</div>
          </div>
          <div style={statCardStyle}>
            <div style={labelStyle}>Free Transfers</div>
            <div style={{ fontWeight: 800 }}>
              {leagueConfig?.maxFreeTransfers || "—"}
            </div>
          </div>
          <div style={statCardStyle}>
            <div style={labelStyle}>Members</div>
            <div style={{ fontWeight: 800 }}>{league?.memberCount || "—"}</div>
          </div>
        </div>
        <div style={{ marginTop: "12px", color: "#666", fontSize: "0.92rem" }}>
          Squad Rules and budget are set by league type. Check the FAQ for the full rules.
        </div>
        <div style={{ marginTop: "12px" }}>
          <Button onClick={() => navigate("/faq")}>Open FAQ</Button>
        </div>
      </Panel>

      <Panel style={{ marginBottom: "1rem", maxWidth: "700px" }}>
        <h3 style={{ marginTop: 0, marginBottom: "10px" }}>Invite Teammates</h3>
        <p style={{ color: "#666", marginTop: 0, marginBottom: "6px" }}>
          Share the league code directly or send invite emails with a join link.
        </p>
        <p style={{ color: "#777", fontSize: "0.92rem", marginTop: 0, marginBottom: "12px" }}>Invited teammates can join directly from the email link or enter the league code manually.</p>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: "14px",
          }}
        >
          <div style={{ ...statCardStyle, minWidth: "220px" }}>
            <div style={labelStyle}>League Code</div>
            <div style={{ fontWeight: 900, fontSize: "1.1rem", letterSpacing: "0.04em" }}>
              {league.code || "—"}
            </div>
          </div>
          <Button onClick={handleCopyCode}>Copy Code</Button>
          <Button onClick={handleCopyInviteMessage}>Copy Invite Message</Button>
        </div>

        <div style={{ marginBottom: "8px", fontWeight: 700 }}>Send invite by email</div>
        <textarea
          value={inviteEmails}
          onChange={(e) => setInviteEmails(e.target.value)}
          placeholder="Enter one or more email addresses, separated by commas or line breaks"
          rows={2}
          style={{
            width: "100%",
            borderRadius: "10px",
            border: "1px solid #ddd",
            padding: "12px",
            font: "inherit",
            resize: "vertical",
            boxSizing: "border-box",
            marginBottom: "8px",
          }}
        />
        <div style={{ color: "#777", fontSize: "0.9rem", marginTop: "4px", marginBottom: "8px" }}>
          If you were invited by email, the join link will prefill your league code automatically.
        </div>
        <div style={{ color: "#777", fontSize: "0.92rem", marginTop: 0, marginBottom: "12px" }}>
          {parsedEmails.length > 0
            ? `${parsedEmails.length} recipient${parsedEmails.length === 1 ? "" : "s"} ready`
            : "No recipients added yet"}
        </div>

        <div style={{ marginTop: "12px" }}>
          <Button onClick={handleSendInvites} disabled={sendingInvites || parsedEmails.length === 0} variant="pink">
            {sendingInvites ? "Sending..." : "Send Invite"}
          </Button>
        </div>
      </Panel>

    </>
  );
}
